import { useMemo, useRef, useState } from "react";
import { Box, Button } from "@mui/material";
import { TariffCard } from "../../../features/tariff-selection/ui/TariffCard";
import { tariffsStore } from "@src/entities/tariffs/model/store/tariffsStore";
import { useCreateTariff } from "@src/entities/tariffs/model/hooks/useCreateTariff/useCreateTariff";
import { useApplyPromoCode } from "@src/entities/tariffs/model/hooks/useApplyPromoCode/useApplyPromoCode";
import { ConfirmPaymentModal } from "@src/features/payment";
import { useUserInfoMeStore } from "@src/entities/user/model/store/userInfoMeStore";
import {
  dictionaryNamesTariffs,
  INamesTariffs,
} from "@src/features/tariff-selection/ui/utils";
import { TariffTeamForm } from "./TariffTeamForm/TariffTeamForm";
import extendTimeImg from "@src/shared/assets/extend_time_img.svg";
import { getActiveEndDate, isLicenseExpired } from "../lib/license";
import { LicenseInfoBanner } from "./LicenseInfoBanner/LicenseInfoBanner";
import { useNotificationStore } from "@src/shared/components/Notification";

import styles from "./TariffSection.module.scss";
import { EnterpriseTariffBanner } from "./EnterpriseTariffBanner/EnterpriseTariffBanner";

type TariffPromoState = {
  value: string;
  appliedPromoCode: string | null;
  discountedAmount: number | null;
  isApplying: boolean;
};

type PromoStateByTariffId = Record<number, TariffPromoState>;

type PurchaseModalState = {
  open: boolean;
  tariffId: number | null;
  tariffName: string | null;
};

const getDefaultPromoState = (): TariffPromoState => ({
  value: "",
  appliedPromoCode: null,
  discountedAmount: null,
  isApplying: false,
});

export const TariffSection = () => {
  const { mutate: createTariff, isPending } = useCreateTariff();
  const { mutateAsync: applyPromoCodeMutation } = useApplyPromoCode();
  const { showAlert } = useNotificationStore();

  const [modalOpen, setModalOpen] = useState(false);
  const [purchaseModalState, setPurchaseModalState] =
    useState<PurchaseModalState>({
      open: false,
      tariffId: null,
      tariffName: null,
    });
  const [promoStateByTariffId, setPromoStateByTariffId] =
    useState<PromoStateByTariffId>({});

  const tariffsRef = useRef<HTMLDivElement | null>(null);

  const tariffs =
    tariffsStore((state) => state.tariffs)
      ?.sort(
        (a, b) =>
          dictionaryNamesTariffs[a.name as INamesTariffs]?.order -
          dictionaryNamesTariffs[b.name as INamesTariffs]?.order,
      )
      ?.filter((el) => el.name !== ("START" as INamesTariffs)) || [];
  const user = useUserInfoMeStore((state) => state.user);
  const linkCurrentTariff = tariffsStore((state) => state.linkCurrentTariff);

  const purchasedTariff = tariffs.find(
    ({ name }) => name === user?.package.name,
  );

  const endDate = getActiveEndDate(user?.last_license);
  const isTrial = user?.package?.name === "FREE_TRIAL";
  const isExpired = isLicenseExpired(endDate);

  const visibleTariffs = useMemo(
    () => tariffs.filter((tariff) => tariff.name !== "FREE_TRIAL"),
    [tariffs],
  );

  const getPromoState = (tariffId: number): TariffPromoState =>
    promoStateByTariffId[tariffId] || getDefaultPromoState();

  const updatePromoState = (
    tariffId: number,
    updater: (prev: TariffPromoState) => TariffPromoState,
  ) => {
    setPromoStateByTariffId((prev) => ({
      ...prev,
      [tariffId]: updater(prev[tariffId] || getDefaultPromoState()),
    }));
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleClosePurchaseModal = () => {
    setPurchaseModalState({
      open: false,
      tariffId: null,
      tariffName: null,
    });
  };

  const handleChooseTariff = () => {
    tariffsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const handleCreatePayment = (
    packageId: number,
    promoCode?: string | null,
  ) => {
    const returnUrl = window.location.origin;

    const params = {
      package_id: packageId,
      description: "Оплата выбранного пакета проверок",
      current_url: returnUrl,
      ...(promoCode ? { promo_code: promoCode } : {}),
    };

    createTariff(params, {
      onSuccess: () => setModalOpen(true),
    });
  };

  const handleExtendTariff = () => {
    if (!purchasedTariff) return;

    const promoState = getPromoState(purchasedTariff.id);

    if (purchasedTariff.name === "STARTER") {
      handleCreatePayment(purchasedTariff.id, promoState.appliedPromoCode);
      return;
    }

    setPurchaseModalState({
      open: true,
      tariffId: purchasedTariff.id,
      tariffName: purchasedTariff.name,
    });
  };

  const handlePromoInputChange = (tariffId: number, value: string) => {
    updatePromoState(tariffId, (prev) => ({
      ...prev,
      value,
    }));
  };

  const handleApplyPromo = async (tariffId: number) => {
    const promoState = getPromoState(tariffId);
    const promoCode = promoState.value.trim();

    if (!promoCode) {
      showAlert("Введите промокод", "error");
      return;
    }

    updatePromoState(tariffId, (prev) => ({
      ...prev,
      isApplying: true,
    }));

    try {
      const response = await applyPromoCodeMutation({
        package_id: tariffId,
        code: promoCode,
      });

      updatePromoState(tariffId, (prev) => ({
        ...prev,
        value: response.code,
        appliedPromoCode: response.code,
        discountedAmount: response.discounted_price,
        isApplying: false,
      }));

      showAlert("Промокод успешно применён", "success");
    } catch {
      updatePromoState(tariffId, (prev) => ({
        ...prev,
        isApplying: false,
      }));
    }
  };

  const handleResetPromo = (tariffId: number) => {
    setPromoStateByTariffId((prev) => ({
      ...prev,
      [tariffId]: getDefaultPromoState(),
    }));

    showAlert("Промокод отменён", "success");
  };

  const handleStartPurchase = (tariffId: number, tariffName: string) => {
    const promoState = getPromoState(tariffId);

    if (promoState.isApplying) return;

    if (tariffName === "START") {
      handleCreatePayment(tariffId, promoState.appliedPromoCode);
      return;
    }

    if (
      tariffName === "TEAM" ||
      tariffName === "BUSINESS" ||
      tariffName === "CORPORATE"
    ) {
      setPurchaseModalState({
        open: true,
        tariffId,
        tariffName,
      });
    }
  };

  const handlePurchaseFormSuccess = () => {
    if (!purchaseModalState.tariffId) return;

    const promoState = getPromoState(purchaseModalState.tariffId);
    const selectedTariffId = purchaseModalState.tariffId;

    handleClosePurchaseModal();
    handleCreatePayment(selectedTariffId, promoState.appliedPromoCode);
  };

  const isTariffs = Array.isArray(tariffs);
  const limitIsOver =
    (user?.last_license?.used_request ?? 0) >=
    (user?.last_license?.available_attempts ?? 0);

  const handleEnterpriseClick = () => {
    window.open(
      "mailto:support@naimus.ai?subject=Вопрос по приобретению тарифа Enterprise&body=Здравствуйте!",
      "_blank",
    );
  };

  return (
    <>
      <div className={styles.currentTariff}>
        <div className={styles.tariff}>
          <p className={styles.tariffTitle}>
            Тариф:{" "}
            <span className={styles.titleName}>
              {dictionaryNamesTariffs[user?.package?.name as INamesTariffs]
                ?.title || "-"}
            </span>
            {isExpired && <span className={styles.endedTariff}> (Истёк)</span>}
          </p>

          {!isTrial && (
            <Button
              variant="text"
              size="small"
              className={styles.extendBtn}
              onClick={handleExtendTariff}
              startIcon={
                <Box
                  component="img"
                  src={extendTimeImg}
                  alt=""
                  aria-hidden="true"
                  sx={{ width: 16, height: 16 }}
                />
              }
              sx={{ p: 0, minWidth: "auto", textTransform: "none" }}
            >
              Продлить тариф
            </Button>
          )}

          <span className={styles.numberAttempts}>
            Попытки:
            <span>
              {limitIsOver
                ? "Лимит исчерпан"
                : `${user?.last_license.used_request} из ${user?.last_license.available_attempts}`}
            </span>
          </span>
        </div>
      </div>

      <LicenseInfoBanner
        endDate={endDate}
        isTrial={isTrial}
        onExtend={handleExtendTariff}
        onChooseTariff={handleChooseTariff}
      />

      <Box className={styles.wrapper} ref={tariffsRef}>
        <h4>Выбрать новый тариф</h4>

        <div className={styles.tariffs}>
          {isTariffs &&
            visibleTariffs.map((tariff, index) => {
              const promoState = getPromoState(tariff.id);

              return (
                <TariffCard
                  key={tariff.id || index}
                  tariff={tariff}
                  promoValue={promoState.value}
                  appliedPromoCode={promoState.appliedPromoCode}
                  discountedAmount={promoState.discountedAmount}
                  isPromoApplying={promoState.isApplying}
                  isPayDisabled={promoState.isApplying}
                  onPromoChange={(value) =>
                    handlePromoInputChange(tariff.id, value)
                  }
                  onApplyPromo={() => handleApplyPromo(tariff.id)}
                  onResetPromo={() => handleResetPromo(tariff.id)}
                  onSelect={() => handleStartPurchase(tariff.id, tariff.name)}
                />
              );
            })}
          <div className={styles.emptyBlock}>em</div>
        </div>
        <EnterpriseTariffBanner onClick={handleEnterpriseClick} />
      </Box>

      <TariffTeamForm
        open={purchaseModalState.open}
        onClose={handleClosePurchaseModal}
        handlePayClickTeam={handlePurchaseFormSuccess}
        tariffId={purchaseModalState.tariffId}
      />

      <ConfirmPaymentModal
        open={modalOpen}
        onClose={handleCloseModal}
        isLoading={isPending}
        paymentUrl={linkCurrentTariff || ""}
      />
    </>
  );
};
